/* eslint-disable react/destructuring-assignment */

import { Select } from "antd";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLoc, useSdk } from "../lib/hooks";

import { UtmTagFilter } from "./UtmTagsSelector";

type Props = {
  filter: Partial<UtmTagFilter>;
  value: string[];
  onChange: (value: string[]) => void;
};

type ValueType = {
  key?: string;
  label: React.ReactNode;
  value: string | number;
};

export const UtmTagSelect: React.FC<Props> = (props) => {
  const sdk = useSdk();
  const [isFetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const loc = useLoc();

  const fetchOptions = useCallback(
    async (searchString: string) => {
      const filterToFetch = new UtmTagFilter(props.filter);
      filterToFetch.update({ search: searchString });
      const result = await sdk.api.partnership.utmTagApi.fetch(
        undefined,
        filterToFetch,
      );

      return result.body.items.map((item) => ({
        key: item,
        label: item,
        value: item,
      }));
    },
    [props.filter, sdk.api.partnership.utmTagApi],
  );

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, 300);
  }, [fetchOptions]);

  useEffect(() => {
    fetchOptions("");
  }, [fetchOptions]);

  return (
    <Select
      showSearch
      labelInValue
      loading={isFetching}
      filterOption={false}
      onSearch={debounceFetcher}
      placeholder={loc.word("UTMTagsSelector.placeholder", {
        default: "Select UTM tags",
      })}
      {...props}
      options={options}
    />
  );
};
